import type { NuxtConfig } from '@nuxt/types'
import VueI18n from 'vue-i18n'
import { Store } from 'vuex'
import type { AxiosStatic } from 'axios'
import { defineNuxtPlugin, type NuxtApp } from 'nuxt/app'
import { UserCarrier } from '~/carrier/user/UserCarrier'
import { AppPublicApiFactory } from '~/plugins/api/app-public-api-factory'
import { AuthApiFactory } from '~/plugins/api/auth-api-factory'
import { AuthJsonApiFactory } from '~/plugins/api/auth-json-api-factory'
import { AppApiFactory } from '~/plugins/api/app-api-factory'
import type { IApiFactoryConstructor } from '~/plugins/api/api-factory/IApiFactory'
import { OrganizationCarrier } from '~/carrier/organizations/OrganizationCarrier'
import type { IRootState } from '~/store'
import { ProductsCarrier } from '~/carrier/products/ProductsCarrier'
import { DlcApiFactory } from '~/plugins/api/dlc-api-factory'
import { DocumentsCarrier } from '~/carrier/documents/DocumentsCarrier'
import { EtkCarrier } from '~/carrier/etk/EtkCarrier'
import { I18nCarrier } from '~/carrier/i18n/I18nCarrier'
import { NewsCarrier } from '~/carrier/news/NewsCarrier'
import { PrioServiceCarrier } from '~/carrier/prio-service/PrioServiceCarrier'
import { ServicePartnerCarrier } from '~/carrier/service-partner/ServicePartnerCarrier'
import { NotificationsCarrier } from '~/carrier/notifications/NotificationsCarrier'
import { SubscriptionsCarrier } from '~/carrier/email/SubscriptionsCarrier'
import { ServiceRequestCarrier } from '~/carrier/email/ServiceRequestCarrier'
import type { Plugin } from '~/plugins/plugins'

export interface IApi {
  readonly documents: DocumentsCarrier
  readonly etk: EtkCarrier
  readonly i18n: I18nCarrier
  readonly news: NewsCarrier
  readonly organization: OrganizationCarrier
  readonly prioService: PrioServiceCarrier
  readonly products: ProductsCarrier
  readonly user: UserCarrier
  readonly servicePartner: ServicePartnerCarrier
  readonly notifications: NotificationsCarrier
  readonly mailSubscriptions: SubscriptionsCarrier
  readonly serviceRequest: ServiceRequestCarrier
}

export class ApiPlugin {
  private static readonly NUXT_PLUGIN_KEY = 'api'
  private readonly axios: AxiosStatic
  private readonly config: NuxtConfig
  private readonly store: Store<IRootState>
  private readonly i18n: VueI18n

  constructor(private readonly nuxtApp: NuxtApp) {
    const { $axios, store, $envConfig, $i18n } = nuxtApp
    this.axios = $axios
    this.config = $envConfig
    this.store = store
    this.i18n = $i18n
  }

  static install: Plugin = (nuxtApp) => {
    const apiPlugin = new ApiPlugin(nuxtApp as unknown as NuxtApp)
    nuxtApp.provide(ApiPlugin.NUXT_PLUGIN_KEY, apiPlugin.setup())
  }

  private setup(): IApi {
    const publicApi = this.createApi(AppPublicApiFactory)
    const authApi = this.createApi(AuthApiFactory)
    const authJsonApi = this.createApi(AuthJsonApiFactory)
    const appApi = this.createApi(AppApiFactory)
    const dlcApi = this.createApi(DlcApiFactory)

    return {
      documents: new DocumentsCarrier(dlcApi),
      etk: new EtkCarrier(appApi),
      i18n: new I18nCarrier(appApi),
      news: new NewsCarrier(appApi, this.i18n),
      organization: new OrganizationCarrier(appApi),
      prioService: new PrioServiceCarrier(appApi),
      products: new ProductsCarrier(appApi),
      user: new UserCarrier(authApi, authJsonApi),
      servicePartner: new ServicePartnerCarrier(appApi),
      notifications: new NotificationsCarrier(appApi),
      mailSubscriptions: new SubscriptionsCarrier(publicApi),
      serviceRequest: new ServiceRequestCarrier(publicApi)
    }
  }

  private createApi(ApiFactory: IApiFactoryConstructor) {
    const apiFactory = new ApiFactory(
      this.config,
      this.store,
      this.i18n,
      this.nuxtApp
    )
    return apiFactory.create(this.axios)
  }
}

export default defineNuxtPlugin(ApiPlugin.install)
